import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useKategoriHarga, kodeBawaan, labelKategori, labelDenganKode } from "../lib/kategoriHarga";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { DR_STATUS_LABEL, rupiah } from "../lib/statusLabels";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ChevronLeft, ChevronRight, AlertCircle, Plus, Trash2 } from "lucide-react";
import {
  ITEM_TYPE_TIGA, LABEL_ITEM_TYPE, POLA_BAJU, WARNA_CELANA, JENIS_LENGAN,
  modeHargaPaket, itemTypeTersedia, butuhWarnaCelana, badanItems, badanKonfigurasi,
  badanRoster, alasanRosterTerhalang, memakaiCelana, labelPaket, labelProduk,
  UKURAN_DEWASA, UKURAN_ANAK, isiDariUsulan, bedaDariIsian,
} from "../lib/faKonfigurasi";
import { toast } from "sonner";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";

// U22b: kategori harga = data (`useKategoriHarga`), bukan daftar tetap.
// T21: tangga K106 (`2XL`, bukan `XXL`) — ejaan lama tidak terbaca tabel tambahan ukuran.
const SIZE_CODES = UKURAN_DEWASA;

const METODE_TERIMA = { AMBIL_DI_CABANG: "Ambil sendiri di cabang", TITIP_AMBIL_CABANG: "Titip & ambil di cabang lain",
                        KIRIM_KE_ALAMAT: "Kirim ke alamat" };

function blankItem() {
  return {
    // K177 2.1 (21 Sep 2026): satu baris = satu item_type (Jersey saja / set
    // polos / set full print), satu harga per lembar. Ruas lama di bawahnya
    // tetap dipakai HANYA bila daftar harga wilayah masih bernama lama
    // (`modeHargaPaket` = "LAMA") — transisi K177 §7 sampai seed §6.
    item_type: "",
    product_type: "ATASAN_ONLY",
    qty_atasan: 6,
    size_code: "M",
    // ⛔ item_type DIPILIH dari daftar harga wilayah (PRASYARAT UAT Tahap 1,
    //    11 Sep 2026) — bukan diketik. Nilai awal diisi `isiItemTypeBawaan`
    //    begitu pilihan harga termuat; kategori tanpa harga = kosong.
    item_type_atasan: "",
    fabric_atasan: "",
    item_type_celana: "",
    fabric_celana: "",
    description: "",
    design_ref: "",
  };
}

function blankAdj() {
  return {
    adjustment_type: "SPECIAL_DISCOUNT",
    discount_amount_idr: 0,
    bonus_jersey_qty: 0,
    design_reference: "",
    reason: "",
    source: "CS_KACAB",
    requested_by_owner_id: "",
  };
}

function blankRoster() {
  // K178 F8: per baris = nama/nomor punggung · size (+ size anak / size celana
  // bila berlaku) · lengan; pola/collar/paket/warna celana SERAGAM di kepala FA.
  // T23: gender kosong secara bawaan — tidak ada keputusan yang mewajibkannya, jadi tidak ditebak "LK".
  return { row_no: 1, nameset_punggung: "", nomor_punggung: "", ukuran_dewasa: "M", ukuran_anak: "",
           ukuran_celana: "", gender: "", jenis_lengan: "PENDEK", baris_item: "" };
}

function blankKepala() {
  // K178 F1: SERAGAM dalam satu FA — paket · pola baju · collar · warna celana.
  // package_category diisi dari kategori aktif pertama begitu daftar termuat (U22b).
  return { package_category: "", pola_baju: "REGULER", collar_id: "", warna_celana: "" };
}

function BedaIsian({ beda }) {
  if (!beda?.length) return null;
  return (
    <div className="text-xs rounded border border-warning-soft-border bg-warning-soft text-warning-soft-foreground px-3 py-2" data-testid="fa-wizard-beda-isian">
      <div className="font-medium mb-1">Berbeda dari pilihan customer di Ruang Pesanan:</div>
      <ul className="list-disc pl-4">{beda.map((b) => <li key={b}>{b}</li>)}</ul>
    </div>
  );
}

/** K222 / T66: isian customer (Ruang Pesanan) tampil di langkah 1 — sumber kebenaran FA, termasuk selisih dengan
 *  perkiraan PD dan catatan yang membuat sistem belum bisa menyusun FA sendiri. */
function IsianCustomer({ usulan, busy, onSusun }) {
  if (!usulan) return null;
  if (!usulan.ada_isian || !usulan.dikunci) {
    return (
      <p className="text-sm rounded border border-border px-3 py-2 text-muted-foreground" data-testid="fa-wizard-isian-belum">
        Customer belum mengunci rincian di Ruang Pesanan — FA diisi dari perkiraan PD. Bila customer sudah mengirim
        rincian lewat chat, pastikan jumlah dan daftar nama sesuai sebelum FA dikirim.
      </p>
    );
  }
  const r = usulan.ringkasan || {};
  const baris = [
    ["Warna", [r.warna, r.kombinasi_warna].filter(Boolean).join(" · ")],
    ["Kerah", r.kerah ? `${r.kerah}${usulan.kepala?.collar_nama ? ` → ${usulan.kepala.collar_nama}` : ""}` : ""],
    ["Lengan", r.lengan], ["Pola", r.pola],
    ["Jersey set", r.jumlah_set ? `${r.jumlah_set} (${r.jenis_celana === "POLOS" ? `celana polos ${r.warna_celana === "PUTIH" ? "putih" : "hitam"}` : r.jenis_celana === "FULL_PRINT" ? "celana full print" : "jenis celana belum dipilih"})` : ""],
    ["Jersey atasan saja", r.jumlah_atasan_saja || ""],
    ["Paket harga", r.paket_harga], ["Paket waktu", r.paket_waktu], ["Nama tim", r.nama_tim],
    ["Sponsor", (r.sponsor || []).map((s) => [s.nama, s.posisi].filter(Boolean).join(" — ")).join("; ")],
    ["Cara terima", METODE_TERIMA[r.metode_pengiriman] ? `${METODE_TERIMA[r.metode_pengiriman]}${r.alamat_kirim ? `: ${r.alamat_kirim}` : ""}` : ""],
  ].filter(([, v]) => v !== "" && v != null);
  return (
    <div className="rounded border border-info-soft-border bg-info-soft p-3 space-y-2 text-sm" data-testid="fa-wizard-isian-customer">
      <div className="font-medium">Isian customer (Ruang Pesanan) — FA disusun dari sini · {usulan.jumlah} nama di daftar</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
        {baris.map(([k, v]) => <React.Fragment key={k}><div className="text-muted-foreground">{k}</div><div>{v}</div></React.Fragment>)}
      </div>
      {(usulan.selisih_pd || []).length > 0 && (
        <ul className="list-disc pl-4 text-xs text-warning-soft-foreground" data-testid="fa-wizard-selisih-pd">
          {usulan.selisih_pd.map((s) => <li key={s.kode}>{s.pesan}</li>)}
        </ul>
      )}
      {(usulan.perhatian || []).length > 0 ? (
        <div className="text-xs" data-testid="fa-wizard-perhatian">
          <div className="font-medium">Perlu diputuskan staf sebelum FA dikirim:</div>
          <ul className="list-disc pl-4">{usulan.perhatian.map((p) => <li key={p.kode}>{p.pesan}</li>)}</ul>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={busy} onClick={onSusun} data-testid="fa-wizard-susun-otomatis">Biarkan sistem menyusun FA</Button>
          <span className="text-xs text-muted-foreground">Isian lengkap — FA langsung tampil ke customer untuk dikonfirmasi.</span>
        </div>
      )}
    </div>
  );
}

export default function FAWizard() {
  const [params] = useSearchParams();
  const drId = params.get("dr");
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [step, setStep] = useState(1);
  const [dr, setDr] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [designSignedUrl, setDesignSignedUrl] = useState(null);
  const [collars, setCollars] = useState([]);
  // {kategori: {atasan: [...], celana: [...]}} dari GET /fa-orders/pilihan-harga/wilayah/{region}
  // — sumbernya price_list_items sungguhan; baris harga baru langsung tampil.
  const [pilihanHarga, setPilihanHarga] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const [kepala, setKepala] = useState(blankKepala());
  const kategoriHarga = useKategoriHarga();
  useEffect(() => {
    if (!kategoriHarga.length) return;
    setKepala((k) => (k.package_category ? k : { ...k, package_category: kodeBawaan(kategoriHarga, "") }));
  }, [kategoriHarga]);
  const [items, setItems] = useState([blankItem()]);
  const [adjustments, setAdjustments] = useState([]);
  // Inventaris C20: Owner yang meminta dipilih dari daftar — dulu UUID Owner diketik tangan.
  const [pilihanOwner, setPilihanOwner] = useState([]);
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/fa-orders/pilihan/owner"); setPilihanOwner(r.data.items || []); }
      catch (e) { toast.error(apiErrorMessage(e, "Daftar Owner gagal dimuat.")); setPilihanOwner([]); }
    })();
  }, []);
  const [roster, setRoster] = useState([]);
  const [faNotes, setFaNotes] = useState("");
  // Nama team/rombongan customer — OPSIONAL. Didata sejak FA supaya terpakai
  // terus sampai Tracking Pesanan dan Dashboard Umum tanpa diketik ulang.
  const [namaTeam, setNamaTeam] = useState("");
  // K222 / T17 / T18 / T66: usulan dari isian customer (Ruang Pesanan) — sumber kebenaran FA, bukan angka PD lama.
  const [usulan, setUsulan] = useState(null);
  const [alasanBeda, setAlasanBeda] = useState("");
  // ATP Lapis 2 (K181 P7/P8): PERKIRAAN, bukan deadline — deadline FA tetap
  // ditulis hook DP dari SLA. Mesin tidak pernah menolak memberi tanggal;
  // MERAH bila melampaui SLA paket, CS yang memutuskan menawarkan/menolak.
  const [atp, setAtp] = useState(null);
  const totalQtyAtasan = items.reduce((s, i) => s + Number(i.qty_atasan || 0), 0);
  useEffect(() => {
    if (step !== 5 || !dr?.package_type || totalQtyAtasan <= 0) return;
    let batal = false;
    api.get("/atp/janji", { params: { package_type: dr.package_type, qty: totalQtyAtasan } })
      .then((r) => { if (!batal) setAtp(r.data); })
      .catch(() => { if (!batal) setAtp(null); });
    return () => { batal = true; };
  }, [step, dr?.package_type, totalQtyAtasan]);

  const loadDR = useCallback(async () => {
    if (!drId) { setErr("Buka layar ini dari halaman Permintaan Desain."); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get(`/design-requests/${drId}`);
      setDr(r.data);
      // U2 NASKAH-UAT (27 Sep 2026): baris pertama dulu selalu 6 potong walau PD mencatat jumlah lain (mis. 12) —
      // CS harus ingat mengubahnya. Jumlah awal kini diambil dari PD selama baris itu belum disentuh.
      const qtyPd = Number(r.data.preliminary_qty_atasan) || 0;
      if (qtyPd > 0) setItems((arr) => (arr.length === 1 && Number(arr[0].qty_atasan) === 6 ? [{ ...arr[0], qty_atasan: qtyPd }] : arr));
      if (r.data.region_id) {
        try {
          const ph = await api.get(`/fa-orders/pilihan-harga/wilayah/${r.data.region_id}`);
          const pilihan = ph.data?.pilihan || {};
          setPilihanHarga(pilihan);
        } catch { setPilihanHarga({}); }
      }
      if (r.data.dr_status !== "DESIGN_APPROVED") {
        setErr(`Keadaan PD sekarang "${DR_STATUS_LABEL[r.data.dr_status] || "—"}". PD wajib "${DR_STATUS_LABEL.DESIGN_APPROVED}" dulu.`);
      }
      if (r.data.customer_id) {
        try { const c = await api.get(`/customers/${r.data.customer_id}`); setCustomer(c.data); } catch { /* ignore */ }
      }
      try {
        const u = await api.get(`/fa-orders/usulan-dari-pd/${drId}`);
        setUsulan(u.data);
        if (u.data?.ada_isian && u.data?.dikunci) {
          const s = isiDariUsulan(u.data, {});
          setKepala((k) => ({ ...k, ...s.kepala }));
          const lengkap = s.items.length > 0 && s.items.every((i) => i.item_type);
          if (lengkap) setItems(s.items);
          else if (u.data.jumlah) setItems((arr) => (arr.length === 1 ? [{ ...arr[0], qty_atasan: u.data.jumlah }] : arr));
          if (s.roster.length) setRoster(lengkap ? s.roster : s.roster.map((r) => ({ ...r, baris_item: "" })));
          if (s.namaTeam) setNamaTeam(s.namaTeam);
        }
      } catch { setUsulan(null); }
      // Fetch first design file (final) and signed URL.
      try {
        const df = await api.get("/design-files", { params: { design_request_id: drId } });
        const final = (df.data?.items || []).find((f) => f.is_final_selected) || (df.data?.items || [])[0];
        if (final) {
          const s = await api.get(`/design-files/${final.id}/signed-url`);
          setDesignSignedUrl(s.data?.signed_url || null);
        }
      } catch { /* designer files optional */ }
    } catch (e) {
      setErr(apiErrorMessage(e, "PD gagal dimuat."));
    } finally {
      setLoading(false);
    }
  }, [drId]);

  useEffect(() => { loadDR(); }, [loadDR]);

  useEffect(() => {
    // Collar master (active) for picker — graceful no-master fallback.
    (async () => {
      try {
        const r = await api.get("/fa-orders/pilihan-collar");   // dulu memanggil rute collar yang TIDAK ADA (sensus pintu B1, 23 Sep 2026)
        setCollars(r.data?.items || []);
      } catch { setCollars([]); }
    })();
  }, []);

  /* ⛔ Sebelum 11 Sep 2026 `item_type_*` adalah <Input> bebas yang wajib
     PERSIS sama dengan `price_list_items.item_type`, dan bawaan
     `item_type_celana` kosong dikirim sebagai "CELANA" — bukan baris harga.
     Terukur: CS nol bisa membuat satu FA SET pun tanpa mengetik nilai yang
     nol pernah diberitahukan padanya. Sekarang: pilihan dari daftar harga
     wilayah, nilai awal = pilihan pertama, dan kategori tanpa harga
     dinyatakan SEBABNYA sebelum 400 terjadi. */
  // Paket dipilih SEKALI di kepala FA (K178 F1); tiap baris mewarisinya.
  const paket = kepala.package_category;
  const modeHarga = modeHargaPaket(pilihanHarga, paket);   // "K177" | "LAMA" | null

  function isiItemTypeBawaan(it, pilihan, paketFA) {
    const p = pilihan?.[paketFA];
    const tiga = itemTypeTersedia(pilihan, paketFA);
    const atasan = p?.atasan || [];
    const celana = p?.celana || [];
    return {
      ...it,
      item_type: tiga.includes(it.item_type) ? it.item_type : (tiga[0] || ""),
      item_type_atasan: atasan.includes(it.item_type_atasan) ? it.item_type_atasan : (atasan[0] || ""),
      item_type_celana: celana.includes(it.item_type_celana) ? it.item_type_celana : (celana[0] || ""),
    };
  }

  // Baris mengikuti paket kepala + daftar harga wilayah — satu tempat, apa pun yang
  // termuat lebih dulu (kategori dari master, pilihan harga, atau pilihan Owner/CS).
  useEffect(() => {
    if (!pilihanHarga || !kepala.package_category) return;
    setItems((arr) => arr.map((it) => isiItemTypeBawaan(it, pilihanHarga, kepala.package_category)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pilihanHarga, kepala.package_category]);

  function updateItem(idx, patch) {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function updateKepala(patch) {
    setKepala((k) => {
      const baru = { ...k, ...patch };
      return baru;
    });
  }

  function alasanItemNolBisaDisimpan(it) {
    if (!pilihanHarga) return "Daftar harga wilayah belum termuat.";
    if (!modeHarga) return `Kategori ${labelKategori(kategoriHarga, paket)} belum punya daftar harga di wilayah ini.`;
    if (modeHarga === "K177") {
      if (!it.item_type) return `Kategori ${labelKategori(kategoriHarga, paket)} belum punya harga untuk baris ini di wilayah ini.`;
      return null;
    }
    if (!it.item_type_atasan) return `Kategori ${labelKategori(kategoriHarga, paket)} belum punya harga atasan di wilayah ini.`;
    if (it.product_type === "SET_ATASAN_CELANA" && !it.item_type_celana) return `Kategori ${labelKategori(kategoriHarga, paket)} belum punya harga celana di wilayah ini.`;
    return null;
  }
  const itemsUntukPagar = items.map((it) => (modeHarga === "K177" ? { ...it, product_type: undefined } : { ...it, item_type: "" }));
  const alasanTerhalang = items.map(alasanItemNolBisaDisimpan).find(Boolean)
    || (modeHarga === "K177" && butuhWarnaCelana(itemsUntukPagar) && !kepala.warna_celana
        ? "Ada baris Jersey set celana hitam/putih — pilih warna celana di kepala FA (sekali per FA)." : null)
    || alasanRosterTerhalang(roster, itemsUntukPagar)
    || null;
  // T17: ubahan staf dari pilihan customer → peringatan menetap + alasan wajib saat simpan.
  const beda = bedaDariIsian(usulan, kepala, itemsUntukPagar, roster);
  // T20: minimum paket (K187) terlihat sejak langkah 2, bukan baru saat simpan.
  const minimumPaket = (kategoriHarga.find((c) => c.kode === paket) || {}).minimum_pesanan;
  const diBawahMinimum = minimumPaket && totalQtyAtasan > 0 && totalQtyAtasan < minimumPaket;
  const alasanSimpan = alasanTerhalang
    || (diBawahMinimum ? `Jumlah di bawah minimum kategori (${minimumPaket} lembar).` : null)
    || (beda.length && alasanBeda.trim().length < 10
        ? "FA berbeda dari pilihan customer — tulis alasannya (minimal 10 huruf) sebelum menyimpan." : null);

  // K222 butir 99b(3): sistem menyusun FA dari isian customer (jalur yang sama dengan pemicu Ruang Pesanan).
  async function susunOtomatis() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post("/fa-orders/susun-dari-pd", { design_request_id: drId });
      if (r.data?.fa_id) {
        toast.success(r.data.dikirim ? "FA disusun sistem dan sudah tampil ke customer." : "FA disusun sistem — periksa catatannya sebelum dikirim.");
        navigate(`/fa-orders/${r.data.fa_id}`);
        return;
      }
      const sebab = (r.data?.perhatian || []).map((p) => p.pesan).join(" ") || "Isian customer belum bisa disusun otomatis.";
      toast.error(sebab);
    } catch (e) {
      setErr(apiErrorMessage(e, "FA gagal disusun sistem."));
    } finally {
      setBusy(false);
    }
  }

  async function saveDraft() {
    setBusy(true);
    setErr(null);
    try {
      // FA-DRAFT-YATIM-SAAT-ITEM-DITOLAK-01 (22 Sep 2026): kepala + konfigurasi
      // (K178 F3/F4) + baris pesanan dikirim dalam SATU permintaan — backend
      // menghitung seluruh baris SEBELUM kepala ditulis, jadi item yang ditolak
      // (harga/minimum/kombinasi) = 400 tanpa FA yatim dan tanpa DR terkunci.
      const konfig = badanKonfigurasi(kepala);
      const itemsBody = badanItems(itemsUntukPagar, kepala);
      const draftR = await api.post("/fa-orders/draft", {
        design_request_id: drId,
        notes: faNotes || null,
        nama_team: namaTeam.trim() || null,
        konfigurasi: Object.keys(konfig).length ? konfig : null,
        items: itemsBody,
        beda_isian: beda.length ? beda : null,
        alasan_beda_isian: beda.length ? alasanBeda.trim() : null,
      });
      const fa = draftR.data;
      const itemIds = (fa?.items || []).map((x) => x.id);
      // Adjustments (optional)
      for (const a of adjustments) {
        if (!a.reason) continue;
        await api.post(`/fa-orders/${fa.id}/adjustments`, {
          ...a,
          discount_amount_idr: a.discount_amount_idr ? Number(a.discount_amount_idr) : null,
          bonus_jersey_qty: a.bonus_jersey_qty ? Number(a.bonus_jersey_qty) : null,
          requested_by_owner_id: a.source === "OWNER_INSTRUCTION" ? (a.requested_by_owner_id || null) : null,
        });
      }
      // Roster (optional)
      if (roster.length) {
        // F5 collar diisi sistem · F6 ukuran celana bila memakai celana · F7 menunjuk baris pesanan.
        const entries = badanRoster(roster, itemIds, itemsUntukPagar);
        await api.post(`/fa-orders/${fa.id}/roster`, { entries });
      }
      navigate(`/fa-orders/${fa.id}`);
    } catch (e) {
      setErr(apiErrorMessage(e, "Draf FA gagal disimpan."));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Memuat...</div>;
  if (err && (!dr || dr.dr_status !== "DESIGN_APPROVED")) {
    return (
      <div className="max-w-2xl">
        <div className="p-4 rounded border border-danger-soft-border bg-danger-soft text-danger-soft-foreground text-sm flex items-start gap-2" data-testid="fa-wizard-error-gate">
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold mb-1">Tidak bisa membuat FA dari PD ini.</div>
            <div>{err}</div>
          </div>
        </div>
      </div>
    );
  }

  const stepHeader = ["1. Periksa PD + Desain", "2. Item + Harga", "3. Penyesuaian Khusus", "4. Roster Jersey", "5. Periksa & Simpan"];

  return (
    <div className="max-w-5xl space-y-6" data-testid="fa-wizard-root">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Buat FA dari Permintaan Desain</h1>
        <Button variant="ghost" onClick={() => navigate(`/design-requests/${drId}`)}>← Kembali ke PD</Button>
      </div>
      <ol className="flex flex-wrap gap-2 text-xs">
        {stepHeader.map((s, i) => (
          <li key={s} className={`px-3 py-1 rounded border ${step === i + 1 ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"}`}>
            {s}
          </li>
        ))}
      </ol>

      {step === 1 && (
        <Card className="p-6 space-y-4" data-testid="fa-wizard-step-1">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-xs text-muted-foreground">Nomor PD</div><div className="font-medium">{dr?.dr_number}</div></div>
            <div><div className="text-xs text-muted-foreground">Customer</div><div className="font-medium">{customer?.name || "—"}</div></div>
            <div><div className="text-xs text-muted-foreground">Kota Customer</div><div>{customer?.city || "—"}</div></div>
            <div><div className="text-xs text-muted-foreground">Produk</div><div>{labelProduk(dr?.product_summary_type)}</div></div>
            <div><div className="text-xs text-muted-foreground">Paket Waktu</div><div>{labelPaket(dr?.package_type)} ({dr?.package_days} hari kerja)</div></div>
            <div><div className="text-xs text-muted-foreground">Perkiraan awal atasan (saat PD dibuat)</div><div>{dr?.preliminary_qty_atasan}</div></div>
            <div><div className="text-xs text-muted-foreground">Perkiraan awal celana (saat PD dibuat)</div><div>{dr?.preliminary_qty_celana}</div></div>
          </div>
          <IsianCustomer usulan={usulan} busy={busy} onSusun={susunOtomatis} />
          {(usulan?.info || []).filter((i) => i.kode === "BERKAS_FINAL_BELUM_ADA").map((i) => (
            <p key={i.kode} className="text-sm rounded border border-warning-soft-border bg-warning-soft text-warning-soft-foreground px-3 py-2" data-testid="fa-wizard-final-belum">
              {i.pesan} FA boleh disusun, tetapi produksi butuh berkas final — ingatkan desainer.
            </p>
          ))}
          <div>
            <div className="text-xs text-muted-foreground mb-2">Desain Final</div>
            {designSignedUrl ? (
              <a href={designSignedUrl} target="_blank" rel="noopener noreferrer" className="inline-block border border-border rounded overflow-hidden hover:border-ring" data-testid="fa-wizard-design-link">
                <img src={designSignedUrl} alt="Final design" className="max-h-64" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <div className="px-3 py-1.5 text-xs text-muted-foreground bg-muted/50">Buka berkas</div>
              </a>
            ) : (
              <div className="text-xs text-muted-foreground italic">Tidak ada berkas final dari desainer. Lanjut saja kalau Anda sudah memeriksanya di luar sistem.</div>
            )}
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card className="p-6 space-y-4" data-testid="fa-wizard-step-2">
          <div>
            <h3 className="font-semibold flex items-center gap-1">Kepala FA — berlaku untuk SELURUH pesanan <Hint>Kategori harga, pola baju, kerah, dan warna celana wajib sama dalam satu FA. Jenis, lengan, dan ukuran boleh beda per baris.</Hint></h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="fa-wizard-kepala">
            <div>
              <Label className="text-xs">Kategori harga</Label>
              <select value={kepala.package_category} onChange={(e) => updateKepala({ package_category: e.target.value })} className="w-full border border-input rounded px-2 py-1.5 text-sm" data-testid="fa-kepala-paket">
                {!kategoriHarga.length && <option value="">— memuat kategori —</option>}
                {kategoriHarga.map((c) => <option key={c.kode} value={c.kode}>{labelDenganKode(kategoriHarga, c.kode)}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Pola Baju</Label>
              <select value={kepala.pola_baju} onChange={(e) => updateKepala({ pola_baju: e.target.value })} className="w-full border border-input rounded px-2 py-1.5 text-sm" data-testid="fa-kepala-pola">
                {POLA_BAJU.map((c) => <option key={c} value={c}>{c === "RAGLAN" ? "Raglan" : "Reguler"}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Collar</Label>
              <select value={kepala.collar_id || ""} onChange={(e) => updateKepala({ collar_id: e.target.value || "" })} className="w-full border border-input rounded px-2 py-1.5 text-sm" data-testid="fa-kepala-collar">
                <option value="">— tanpa collar khusus —</option>
                {collars.map((c) => <option key={c.id} value={c.id}>{c.collar_name || c.collar_code}{c.surcharge_amount_idr ? ` (+${rupiah(c.surcharge_amount_idr)})` : ""}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Warna Celana{butuhWarnaCelana(itemsUntukPagar) ? " *" : ""}</Label>
              <select value={kepala.warna_celana} onChange={(e) => updateKepala({ warna_celana: e.target.value })} className="w-full border border-input rounded px-2 py-1.5 text-sm" data-testid="fa-kepala-warna-celana">
                <option value="">— (hanya untuk set celana hitam/putih) —</option>
                {WARNA_CELANA.map((c) => <option key={c} value={c}>{c === "HITAM" ? "Hitam" : "Putih"}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <h3 className="font-semibold">Baris Pesanan</h3>
            <Button size="sm" variant="outline" onClick={() => setItems((a) => [...a, isiItemTypeBawaan(blankItem(), pilihanHarga, paket)])}><Plus size={14} className="mr-1" />Tambah Baris</Button>
          </div>
          {modeHarga === null && (
            <p className="text-xs rounded border border-warning-soft-border bg-warning-soft text-warning-soft-foreground px-3 py-2">Kategori {labelKategori(kategoriHarga, paket)} belum punya daftar harga di wilayah ini — minta Owner/Kepala Cabang mengisinya di Master Harga.</p>
          )}
          {diBawahMinimum && (
            <p className="text-xs rounded border border-warning-soft-border bg-warning-soft text-warning-soft-foreground px-3 py-2" data-testid="fa-wizard-minimum">
              Jumlah {totalQtyAtasan} lembar di bawah minimum kategori {labelKategori(kategoriHarga, paket)} ({minimumPaket} lembar). FA tidak bisa disimpan sampai jumlahnya cukup atau kategorinya diganti.
            </p>
          )}
          <BedaIsian beda={beda} />
          {items.map((it, idx) => (
            <div key={idx} className="border border-border rounded p-4 space-y-3" data-testid={`fa-wizard-item-${idx}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Baris #{idx + 1}</div>
                {items.length > 1 && (
                  <button onClick={() => setItems((a) => a.filter((_, i) => i !== idx))} className="text-danger hover:text-danger/80"><Trash2 size={14} /></button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                {modeHarga !== "LAMA" ? (
                  <div>
                    <Label className="text-xs">Jenis</Label>
                    <select value={it.item_type} onChange={(e) => updateItem(idx, { item_type: e.target.value })}
                            className="w-full border border-input rounded px-2 py-1.5 text-sm" data-testid="fa-item-type"
                            disabled={!itemTypeTersedia(pilihanHarga, paket).length}>
                      {itemTypeTersedia(pilihanHarga, paket).length === 0 && <option value="">— belum ada harga —</option>}
                      {ITEM_TYPE_TIGA.filter((t) => itemTypeTersedia(pilihanHarga, paket).includes(t)).map((t) => <option key={t} value={t}>{LABEL_ITEM_TYPE[t]}</option>)}
                    </select>
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs">Tipe Produk</Label>
                    <select value={it.product_type} onChange={(e) => updateItem(idx, { product_type: e.target.value })} className="w-full border border-input rounded px-2 py-1.5 text-sm">
                      <option value="ATASAN_ONLY">Atasan Saja</option>
                      <option value="SET_ATASAN_CELANA">Set Atasan + Celana</option>
                    </select>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Jumlah (lembar)</Label>
                  <Input type="number" min="1" value={it.qty_atasan} onChange={(e) => updateItem(idx, { qty_atasan: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Ukuran</Label>
                  <select value={it.size_code || ""} onChange={(e) => updateItem(idx, { size_code: e.target.value || null })} className="w-full border border-input rounded px-2 py-1.5 text-sm">
                    <option value="">— (tanpa tambahan ukuran)</option>
                    {SIZE_CODES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {modeHarga === "LAMA" && (
                  <>
                    <div>
                      <Label className="text-xs">Jenis Atasan</Label>
                      <select value={it.item_type_atasan} onChange={(e) => updateItem(idx, { item_type_atasan: e.target.value })}
                              className="w-full border border-input rounded px-2 py-1.5 text-sm" data-testid="fa-item-type-atasan"
                              disabled={!(pilihanHarga?.[paket]?.atasan || []).length}>
                        {(pilihanHarga?.[paket]?.atasan || []).length === 0 && <option value="">— belum ada harga —</option>}
                        {(pilihanHarga?.[paket]?.atasan || []).map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Kain Atasan (opsional)</Label>
                      <Input value={it.fabric_atasan} onChange={(e) => updateItem(idx, { fabric_atasan: e.target.value })} placeholder="DRY_FIT, ..." />
                    </div>
                    {it.product_type === "SET_ATASAN_CELANA" && (
                      <>
                        <div>
                          <Label className="text-xs">Jenis Celana</Label>
                          <select value={it.item_type_celana} onChange={(e) => updateItem(idx, { item_type_celana: e.target.value })}
                                  className="w-full border border-input rounded px-2 py-1.5 text-sm" data-testid="fa-item-type-celana"
                                  disabled={!(pilihanHarga?.[paket]?.celana || []).length}>
                            {(pilihanHarga?.[paket]?.celana || []).length === 0 && <option value="">— belum ada harga —</option>}
                            {(pilihanHarga?.[paket]?.celana || []).map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">Kain Celana</Label>
                          <Input value={it.fabric_celana} onChange={(e) => updateItem(idx, { fabric_celana: e.target.value })} />
                        </div>
                      </>
                    )}
                  </>
                )}
                <div className="col-span-full">
                  <Label className="text-xs">Deskripsi</Label>
                  <Input value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} placeholder="opsional" />
                </div>
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground italic flex items-center gap-1">Harga dihitung saat Simpan Draf (Langkah 5). <Hint>Harga diambil dari daftar harga wilayah, satu harga per lembar (set sudah termasuk celana). Tidak ada pratinjau angka.</Hint></p>
        </Card>
      )}

      {step === 3 && (
        <Card className="p-6 space-y-4" data-testid="fa-wizard-step-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Penyesuaian Khusus (opsional)</h3>
            <Button size="sm" variant="outline" onClick={() => setAdjustments((a) => [...a, blankAdj()])}><Plus size={14} className="mr-1" />Tambah</Button>
          </div>
          {adjustments.length === 0 && <p className="text-xs text-muted-foreground italic">Belum ada penyesuaian. Lewati langkah ini kalau tidak perlu.</p>}
          {adjustments.map((a, idx) => (
            <div key={idx} className="border border-border rounded p-4 space-y-3" data-testid={`fa-wizard-adj-${idx}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Penyesuaian #{idx + 1}</div>
                <button onClick={() => setAdjustments((arr) => arr.filter((_, i) => i !== idx))} className="text-danger hover:text-danger/80"><Trash2 size={14} /></button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs">Jenis</Label>
                  <select value={a.adjustment_type} onChange={(e) => setAdjustments((arr) => arr.map((x, i) => i === idx ? { ...x, adjustment_type: e.target.value } : x))} className="w-full border border-input rounded px-2 py-1.5 text-sm">
                    <option value="SPECIAL_DISCOUNT">Diskon khusus</option>
                    <option value="BONUS_JERSEY">Bonus jersey</option>
                    <option value="BOTH">Diskon + bonus jersey</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Sumber</Label>
                  <select value={a.source} onChange={(e) => setAdjustments((arr) => arr.map((x, i) => i === idx ? { ...x, source: e.target.value } : x))} className="w-full border border-input rounded px-2 py-1.5 text-sm">
                    <option value="CS_KACAB">Inisiatif CS/Kepala Cabang → disetujui Kepala Cabang</option>
                    <option value="OWNER_INSTRUCTION">Instruksi Owner → disetujui Owner</option>
                  </select>
                </div>
                {a.adjustment_type !== "BONUS_JERSEY" && (
                  <div>
                    <Label className="text-xs">Diskon (Rp)</Label>
                    <Input type="number" value={a.discount_amount_idr} onChange={(e) => setAdjustments((arr) => arr.map((x, i) => i === idx ? { ...x, discount_amount_idr: e.target.value } : x))} />
                  </div>
                )}
                {a.adjustment_type !== "SPECIAL_DISCOUNT" && (
                  <div>
                    <Label className="text-xs">Jumlah Jersey Bonus</Label>
                    <Input type="number" value={a.bonus_jersey_qty} onChange={(e) => setAdjustments((arr) => arr.map((x, i) => i === idx ? { ...x, bonus_jersey_qty: e.target.value } : x))} />
                  </div>
                )}
                {a.source === "OWNER_INSTRUCTION" && (
                  <div className="col-span-full">
                    <Label className="text-xs">Owner yang meminta</Label>
                    <select className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" value={a.requested_by_owner_id || ""}
                            data-testid={`fa-adj-owner-${idx}`}
                            onChange={(e) => setAdjustments((arr) => arr.map((x, i) => i === idx ? { ...x, requested_by_owner_id: e.target.value } : x))}>
                      <option value="">— pilih Owner —</option>
                      {pilihanOwner.map((o) => <option key={o.id} value={o.id}>{o.nama}</option>)}
                    </select>
                  </div>
                )}
                <div className="col-span-full">
                  <Label className="text-xs">Alasan (wajib)</Label>
                  <Textarea value={a.reason} onChange={(e) => setAdjustments((arr) => arr.map((x, i) => i === idx ? { ...x, reason: e.target.value } : x))} rows={2} />
                </div>
              </div>
            </div>
          ))}
        </Card>
      )}

      {step === 4 && (
        <Card className="p-6 space-y-4" data-testid="fa-wizard-step-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Roster Jersey (opsional)</h3>
            <Button size="sm" variant="outline" onClick={() => setRoster((a) => [...a, { ...blankRoster(), row_no: a.length + 1, baris_item: items.length === 1 ? "0" : "" }])}><Plus size={14} className="mr-1" />Tambah Baris</Button>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1">Per orang: nama & nomor punggung · ukuran (+ ukuran anak / ukuran celana bila berlaku) · lengan. <Hint>Pola baju, kerah, dan paket ikut kepala FA — diisi sistem.</Hint></p>
          {roster.length === 0 && <p className="text-xs text-muted-foreground italic">Belum ada baris roster. Boleh dilewati dan diisi nanti di detail FA.</p>}
          {roster.length > 0 && (
            <table className="w-full text-sm" data-testid="fa-wizard-roster-table">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr><th className="text-left py-2">No</th><th className="text-left">Nama Punggung</th><th className="text-left">Nomor</th>
                    {items.length > 1 && <th className="text-left">Baris Pesanan</th>}
                    <th className="text-left">Ukuran</th><th className="text-left">Ukuran Anak</th><th className="text-left">Ukuran Celana</th>
                    <th className="text-left">Lengan</th><th className="text-left">Gender</th><th></th></tr>
              </thead>
              <tbody>
                {roster.map((r, idx) => {
                  const bi = r.baris_item === "" || r.baris_item == null ? (items.length === 1 ? 0 : null) : Number(r.baris_item);
                  const pakaiCelana = bi != null && memakaiCelana(itemsUntukPagar[bi]);
                  const upd = (patch) => setRoster((arr) => arr.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
                  return (
                    <tr key={idx} className="border-b border-border" data-testid={`fa-wizard-roster-${idx}`}>
                      <td className="py-2">{idx + 1}</td>
                      <td><Input value={r.nameset_punggung} onChange={(e) => upd({ nameset_punggung: e.target.value })} /></td>
                      <td><Input value={r.nomor_punggung} onChange={(e) => upd({ nomor_punggung: e.target.value })} /></td>
                      {items.length > 1 && (
                        <td>
                          <select value={r.baris_item ?? ""} onChange={(e) => upd({ baris_item: e.target.value })} className="border border-input rounded px-2 py-1 text-sm" data-testid="fa-roster-baris-item">
                            <option value="">— pilih —</option>
                            {items.map((it, i) => <option key={i} value={i}>#{i + 1} {modeHarga === "LAMA" ? (it.product_type === "SET_ATASAN_CELANA" ? "Set" : "Atasan") : (LABEL_ITEM_TYPE[it.item_type] || "—")}</option>)}
                          </select>
                        </td>
                      )}
                      <td>
                        <select value={r.ukuran_dewasa || ""} onChange={(e) => upd({ ukuran_dewasa: e.target.value, ...(e.target.value ? { ukuran_anak: "" } : {}) })} className="border border-input rounded px-2 py-1 text-sm">
                          <option value="">—</option>
                          {SIZE_CODES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td>
                        <select value={r.ukuran_anak || ""} onChange={(e) => upd({ ukuran_anak: e.target.value, ...(e.target.value ? { ukuran_dewasa: "" } : {}) })} className="border border-input rounded px-2 py-1 text-sm" data-testid="fa-roster-ukuran-anak">
                          <option value="">—</option>
                          {UKURAN_ANAK.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td>
                        {pakaiCelana
                          ? <Input value={r.ukuran_celana || ""} onChange={(e) => upd({ ukuran_celana: e.target.value })} placeholder="wajib" className="w-20" data-testid="fa-roster-ukuran-celana" />
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td>
                        <select value={r.jenis_lengan || "PENDEK"} onChange={(e) => upd({ jenis_lengan: e.target.value })} className="border border-input rounded px-2 py-1 text-sm">
                          {JENIS_LENGAN.map((s) => <option key={s} value={s}>{s === "PENDEK" ? "Pendek" : "Panjang"}</option>)}
                        </select>
                      </td>
                      <td>
                        <select value={r.gender || ""} onChange={(e) => upd({ gender: e.target.value })} className="border border-input rounded px-2 py-1 text-sm">
                          <option value="">—</option>
                          <option value="LK">LK</option><option value="PR">PR</option><option value="UNISEX">UNISEX</option>
                        </select>
                      </td>
                      <td><button onClick={() => setRoster((arr) => arr.filter((_, i) => i !== idx))} className="text-danger hover:text-danger/80"><Trash2 size={14} /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {step === 5 && (
        <Card className="p-6 space-y-4" data-testid="fa-wizard-step-5">
          <h3 className="font-semibold">Periksa & Simpan Draf</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-muted-foreground">Customer:</span> {customer?.name}{customer?.city ? ` — ${customer.city}` : ""}</div>
            <div><span className="text-muted-foreground">PD:</span> {dr?.dr_number}</div>
            <div><span className="text-muted-foreground">Baris item:</span> {items.length}</div>
            <div><span className="text-muted-foreground">Total qty:</span> {items.reduce((s, i) => s + Number(i.qty_atasan || 0), 0)}</div>
            <div><span className="text-muted-foreground">Penyesuaian:</span> {adjustments.length}</div>
            <div><span className="text-muted-foreground">Baris roster:</span> {roster.length}</div>
          </div>
          {atp && (
            <div className={`text-sm rounded border p-3 ${atp.melampaui_sla ? "border-destructive text-destructive" : "border-border"}`} data-testid="fa-wizard-atp">
              <div><span className="text-muted-foreground">Perkiraan selesai:</span> <b>{atp.tanggal_janji}</b>{atp.melampaui_sla ? " — melewati batas waktu paket" : ""}{atp.tanggal_sla && atp.tanggal_sla !== atp.tanggal_janji ? ` · Batas waktu paket: ${atp.tanggal_sla}` : ""}</div>
              <div className="text-xs text-muted-foreground mt-1">{atp.penanda}{atp.melampaui_sla ? " · Sistem tetap memberi tanggal; CS yang memutuskan menawarkan atau menolak." : ""}</div>
            </div>
          )}
          <div>
            <Label className="text-xs flex items-center gap-1">Nama Team Customer (opsional) <Hint>Dipakai di Tracking Pesanan dan Dashboard Umum.</Hint></Label>
            <Input value={namaTeam} onChange={(e) => setNamaTeam(e.target.value)}
                   placeholder="mis. SMA 5 Basket Putra — kosongkan bila pesanan perorangan"
                   data-testid="fa-wizard-nama-team" />
          </div>
          <div>
            <Label className="text-xs">Catatan FA (opsional)</Label>
            <Textarea value={faNotes} onChange={(e) => setFaNotes(e.target.value)} rows={3} />
          </div>
          {err && (
            <div className="text-xs text-danger-soft-foreground bg-danger-soft border border-danger-soft-border rounded p-3 break-words" data-testid="fa-wizard-save-error">{err}</div>
          )}
          {beda.length > 0 && (
            <div className="space-y-1">
              <BedaIsian beda={beda} />
              <Label className="text-xs">Alasan FA berbeda dari pilihan customer (wajib)</Label>
              <Textarea value={alasanBeda} onChange={(e) => setAlasanBeda(e.target.value)} rows={2}
                        placeholder="mis. customer minta ganti kerah lewat chat 26 Okt" data-testid="fa-wizard-alasan-beda" />
            </div>
          )}
          {alasanSimpan && (
            <p className="text-sm text-warning-soft-foreground mr-3" data-testid="fa-wizard-terhalang">{alasanSimpan}</p>
          )}
          <Button onClick={saveDraft} disabled={busy || Boolean(alasanSimpan)} data-testid="fa-wizard-save-btn">
            {busy ? "Menyimpan..." : "Simpan Draf FA"}
          </Button>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}>
          <ChevronLeft size={16} className="mr-1" />Sebelumnya
        </Button>
        {step < 5 ? (
          <Button onClick={() => setStep((s) => Math.min(5, s + 1))} data-testid="fa-wizard-next">
            Berikutnya<ChevronRight size={16} className="ml-1" />
          </Button>
        ) : <div />}
      </div>
    </div>
  );
}
